import React, { useEffect, useState, useRef } from 'react';
import SocialIntegrations from '../customer/Integration/SocialIntegrations';
import { ExternalLink, AlertCircle, CheckCircle, Clock } from 'lucide-react';

const QR_EXPIRATION_TIME = 2 * 60 * 60 * 1000; // match backend (2 hours)

// Robust query parser: prefer explicit search params, fall back to hash query.
const parseQuery = () => {
  // 1) If the URL contains search params (?a=1&b=2), use them
  if (window.location.search && window.location.search.length > 1) {
    try {
      return new URLSearchParams(window.location.search);
    } catch (e) { /* fall through */ }
  }

  // 2) Otherwise, check the hash fragment for query params (#/path?x=1)
  const hash = window.location.hash || '';
  const idx = hash.indexOf('?');
  if (idx !== -1) {
    try {
      return new URLSearchParams(hash.substring(idx + 1));
    } catch (e) { /* fall through */ }
  }

  // 3) Empty params if none found
  return new URLSearchParams();
};

export default function Configure() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expired, setExpired] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [platformKey, setPlatformKey] = useState('');
  const [autoConnect, setAutoConnect] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);

  // NEW: awaiting user gesture to allow opening popup
  const [awaitingUserGesture, setAwaitingUserGesture] = useState(false);
  
  // NEW: show accept/reject confirmation when QR is scanned
  const [showConfirmation, setShowConfirmation] = useState(false);

  const socialRef = useRef(null);

  useEffect(() => {
    const params = parseQuery();
    const customerId = params.get('customerId') || params.get('customerid') || ''; // Also check lowercase
    const platform = params.get('platform') || '';
    const auto = params.get('autoConnect') || params.get('auto') || '';
    const timestamp = params.get('t');
    const source = params.get('source') || '';

    console.log('🔍 Configure URL params:', { customerId, platform, auto, timestamp, source });
    console.log('🔍 Current URL:', window.location.href);
    console.log('🔍 Origin:', window.location.origin);

    if (!customerId || !platform) {
      setError(`Missing required parameters. CustomerId: ${customerId || 'missing'}, Platform: ${platform || 'missing'}`);
      setLoading(false);
      return;
    }

    setPlatformKey(platform);
    // Show accept/reject for QR scans OR explicit autoConnect
    // If timestamp is present, it's likely from a QR code
    const isQrScan = timestamp || source === 'admin-qr-generator' || auto === '1' || auto === 'true';
    setAutoConnect(isQrScan);

    // Use a local timer id and do NOT return early so fetchCustomer runs
    let tId = null;

    if (timestamp) {
      const linkGeneratedAt = parseInt(timestamp, 10);
      if (!isNaN(linkGeneratedAt)) {
        const now = Date.now();
        const age = now - linkGeneratedAt;
        if (age > QR_EXPIRATION_TIME) {
          setExpired(true);
          setLoading(false);
          return;
        }

        // start countdown
        const updateRemaining = () => {
          const rem = linkGeneratedAt + QR_EXPIRATION_TIME - Date.now();
          if (rem <= 0) {
            setTimeRemaining('Expired');
            setExpired(true);
            return;
          }
          const h = Math.floor(rem / (1000 * 60 * 60));
          const m = Math.floor((rem % (1000 * 60 * 60)) / (1000 * 60));
          const s = Math.floor((rem % (1000 * 60)) / 1000);
          setTimeRemaining(`${h}h ${m}m ${s}s`);
        };
        updateRemaining();
        tId = setInterval(updateRemaining, 1000);
      }
    }

    // fetch customer info (try multiple endpoints with fallbacks)
    const fetchCustomer = async () => {
      try {
        setLoading(true);
        setError('');
        
        // ✅ UPDATED: Better API base URL detection for production
        let apiBase = process.env.REACT_APP_API_URL || '';
        
        // If no API URL configured and we're in production, use the production backend
        if (!apiBase && process.env.NODE_ENV === 'production') {
          apiBase = 'https://my-backend-593529385135.asia-south1.run.app';
        }
        
        console.log('🔍 Using API base:', apiBase);

        // helper to attempt a fetch and return parsed JSON or null
        const tryFetchJson = async (url, opts = {}) => {
          try {
            console.log('🔍 Trying fetch:', url);
            const res = await fetch(url, opts);
            if (!res.ok) {
              // still try to parse error body for debugging
              let txt;
              try { txt = await res.text(); } catch (e) { txt = String(e); }
              console.warn('Request failed', url, res.status, txt);
              return { ok: false, status: res.status, bodyText: txt, json: null };
            }
            const json = await res.json();
            console.log('✅ Fetch success:', url, json);
            return { ok: true, status: res.status, json };
          } catch (err) {
            // network error (connection refused, CORS, etc.)
            console.warn('Network fetch error for', url, err.message);
            return null;
          }
        };

        // Try configured API base first (more reliable for production)
        if (apiBase) {
          const remoteById = `${apiBase.replace(/\/$/, '')}/api/customers/${encodeURIComponent(customerId)}`;
          const attemptRemoteById = await tryFetchJson(remoteById);
          if (attemptRemoteById && attemptRemoteById.ok) {
            const fetched = attemptRemoteById.json.customer || attemptRemoteById.json;
            setCustomer(fetched);
            setLoading(false);
            return;
          }
        }

        // Try relative endpoint (works when backend is proxied by frontend dev server)
        const relativeById = `/api/customers/${encodeURIComponent(customerId)}`;
        const attemptRelativeById = await tryFetchJson(relativeById);
        if (attemptRelativeById && attemptRelativeById.ok) {
          const fetched = attemptRelativeById.json.customer || attemptRelativeById.json;
          setCustomer(fetched);
          setLoading(false);
          return;
        }

        // Fallback: try listing endpoints (relative then remote)
        const relativeList = `/api/customers`;
        const attemptRelativeList = await tryFetchJson(relativeList);
        if (attemptRelativeList && attemptRelativeList.ok) {
          const list = attemptRelativeList.json.customers || attemptRelativeList.json;
          const found = Array.isArray(list) ? list.find(c => String(c._id) === String(customerId) || String(c.id) === String(customerId)) : null;
          if (found) {
            setCustomer(found);
            setLoading(false);
            return;
          }
        }

        if (apiBase) {
          const remoteList = `${apiBase.replace(/\/$/, '')}/api/customers`;
          const attemptRemoteList = await tryFetchJson(remoteList);
          if (attemptRemoteList && attemptRemoteList.ok) {
            const list = attemptRemoteList.json.customers || attemptRemoteList.json;
            const found = Array.isArray(list) ? list.find(c => String(c._id) === String(customerId) || String(c.id) === String(customerId)) : null;
            if (found) {
              setCustomer(found);
              setLoading(false);
            
              return;
            }
          }
        }

        setError(`Customer not found. CustomerId: ${customerId}. Please verify the QR was generated for an existing customer or that the API is reachable.`);
      } catch (err) {
        console.error('🚫 Network error:', err);
        setError('Network error while fetching customer.');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();

    // cleanup interval on unmount
    return () => {
      if (tId) clearInterval(tId);
    };
  }, []); // run once on mount

  // REPLACE previous auto-trigger effect with user-gesture flow:
  useEffect(() => {
    // If autoConnect was requested, wait for customer & platform to be loaded,
    // then show accept/reject confirmation dialog
    if (!autoConnect || !customer || !platformKey) return;

    // Show confirmation dialog first
    setShowConfirmation(true);
  }, [autoConnect, customer, platformKey]);
  
  const handleAcceptConnection = () => {
    // User accepted, now show the connect prompt
    setShowConfirmation(false);
    setAwaitingUserGesture(true);
    
    // focus the button shortly after render so mobile users can tap faster
    setTimeout(() => {
      const btn = document.getElementById('auto-connect-btn');
      if (btn) btn.focus();
    }, 300);
  };
  
  const handleRejectConnection = () => {
    // User rejected the connection
    setShowConfirmation(false);
    
    // Show rejection overlay
    const rejectOverlay = document.createElement('div');
    rejectOverlay.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6';
    rejectOverlay.innerHTML = `
      <div class="bg-white rounded-xl p-8 max-w-md w-full text-center shadow-lg">
        <div class="flex justify-center mb-4">
          <svg class="w-16 h-16 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </div>
        <h3 class="text-xl font-semibold mb-2 text-slate-900">Connection Cancelled</h3>
        <p class="text-sm text-slate-600 mb-6">
          The account connection was cancelled. No changes were made.
        </p>
        <p class="text-xs text-slate-500">
          You can now close this window.
        </p>
      </div>
    `;
    document.body.appendChild(rejectOverlay);
    
    // Try to close the window after 2 seconds
    setTimeout(() => {
      window.close();
    }, 2000);
  };

  const mapPlatform = (key) => {
    switch (key) {
      case 'fb': return 'facebook';
      case 'insta': return 'instagram';
      case 'yt': return 'youtube';
      case 'linkedin': return 'linkedin';
      case 'twitter': return 'twitter';
      default: return key; // allow full names as well
    }
  };

  const platform = mapPlatform(platformKey);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-xl w-full bg-white rounded-xl p-6 shadow">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="font-semibold text-red-900">Configuration error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <div className="mt-4">
                <a href="/" className="text-blue-600 hover:underline flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" /> Go to main app
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-xl w-full bg-white rounded-xl p-6 shadow">
          <div className="flex items-start gap-3">
            <Clock className="w-6 h-6 text-amber-600" />
            <div>
              <h3 className="font-semibold text-amber-900">QR Code Expired</h3>
              <p className="text-sm text-amber-700 mt-1">This QR code has expired. For security reasons QR codes expire after a short time.</p>
              <div className="mt-4 space-x-3">
                <a href="/" className="text-blue-600 hover:underline flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" /> Go to main app
                </a>
                <a href="/#/admin/qr-generator" className="text-slate-700 hover:underline flex items-center gap-2">
                  Open Admin QR Generator
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <div className="animate-pulse text-slate-400">Loading configuration...</div>
        </div>
      </div>
    );
  }

  // final render (unchanged surrounding layout) but add overlay when awaitingUserGesture
  return (
    <div className="min-h-screen flex items-start justify-center p-6">
      <div className="w-full max-w-2xl bg-white rounded-xl p-6 shadow">
        <div className="mb-4">
          <h2 className="text-xl font-bold">Configure {platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : 'Social'}</h2>
          {timeRemaining && <p className="text-sm text-slate-600 mt-1">Link valid for: {timeRemaining}</p>}
        </div>

        {/* Social integrations widget */}
        <SocialIntegrations
          ref={socialRef}
          platform={platform}
          customer={customer}
          compact={true}
          onConnectionSuccess={() => {
            // Show success message and close window/tab after QR scan connection
            setAwaitingUserGesture(false);
            setError('');
            
            // Show success overlay
            const successOverlay = document.createElement('div');
            successOverlay.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6';
            successOverlay.innerHTML = `
              <div class="bg-white rounded-xl p-8 max-w-md w-full text-center shadow-lg">
                <div class="flex justify-center mb-4">
                  <svg class="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <h3 class="text-xl font-semibold mb-2 text-green-900">Account Connected Successfully!</h3>
                <p class="text-sm text-slate-600 mb-6">
                  Your ${platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : 'social media'} account has been connected.
                </p>
                <p class="text-xs text-slate-500">
                  This window will close automatically...
                </p>
              </div>
            `;
            document.body.appendChild(successOverlay);
            
            // Close the window/tab after 2 seconds
            setTimeout(() => {
              window.close();
              
              // If window.close() fails (some browsers block it), show a close instruction
              setTimeout(() => {
                successOverlay.innerHTML = `
                  <div class="bg-white rounded-xl p-8 max-w-md w-full text-center shadow-lg">
                    <div class="flex justify-center mb-4">
                      <svg class="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                    </div>
                    <h3 class="text-xl font-semibold mb-2 text-green-900">Connection Complete!</h3>
                    <p class="text-sm text-slate-600 mb-4">
                      Your account has been successfully connected.
                    </p>
                    <p class="text-sm font-medium text-slate-700">
                      You can now close this window.
                    </p>
                  </div>
                `;
              }, 500);
            }, 2000);
          }}
        />
             
        {/* NEW: Accept/Reject confirmation dialog when QR is scanned */}
        {showConfirmation && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-lg">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-blue-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-slate-900">Connect {platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : 'Account'}?</h3>
                <p className="text-sm text-slate-600 mb-2">
                  Do you want to connect your {platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : 'social media'} account?
                </p>
                {customer && (
                  <div className="bg-slate-50 rounded-lg p-3 mt-3">
                    <p className="text-xs text-slate-500 mb-1">Customer</p>
                    <p className="text-sm font-medium text-slate-900">{customer.name || 'Unknown'}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleRejectConnection}
                  className="flex-1 px-6 py-3 rounded-lg bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 transition-colors"
                >
                  Reject
                </button>
                <button
                  onClick={handleAcceptConnection}
                  className="flex-1 px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* NEW: Overlay prompt for autoConnect requiring user gesture */}
        {awaitingUserGesture && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6">
            <div className="bg-white rounded-xl p-6 max-w-md w-full text-center shadow-lg">
              <h3 className="text-lg font-semibold mb-2">Continue to Connect {platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : ''}</h3>
              <p className="text-sm text-slate-600 mb-4">
                To complete authentication we need to open the {platform} sign-in window. Tap the button below to continue.
                This must be initiated by a tap on most mobile browsers.
              </p>

              <button
                id="auto-connect-btn"
                onClick={() => {
                  // hide the prompt and trigger connect inside user gesture
                  setAwaitingUserGesture(false);
                  setTimeout(() => {
                    if (socialRef.current && typeof socialRef.current.triggerConnect === 'function') {
                      socialRef.current.triggerConnect();
                    } else {
                      setError('Automatic trigger not available. Please press Connect in the widget.');
                    }
                  }, 100);
                }}
                className="px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold"
              >
                Tap to Connect {platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : 'Account'}
              </button>

              <div className="mt-3 text-sm text-slate-500">
                <button
                  onClick={() => {
                    setAwaitingUserGesture(false);
                    setError('Auto-connect cancelled by user.');
                  }}
                  className="text-slate-600 hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
